import { FromSchema } from '@40seas/entities/common';
import { RefinementCtx, z } from 'zod';
import { IdentificationType, identificationTypesData } from './identificationType.enum';

export const IdentificationSchema = z
    .object({
        type: z.string().openapi({ enum: Object.values(IdentificationType) }),
        code: z.string(),
    })
    .strict()
    .superRefine(refineIdentificationSchema);

export class Identification extends FromSchema(IdentificationSchema) {
    type: IdentificationType;
    code: string;
}

const formatRegexes: Record<string, { type: string; regex: RegExp }> = {
    N: {
        type: 'numeric',
        regex: /^[\d\s]+$/,
    },
    A: {
        type: 'alphabetic',
        regex: /^[a-zA-Z\s]+$/,
    },
    AN: {
        type: 'alphanumeric',
        regex: /^[a-zA-Z\d\s]+$/,
    },
};

function refineIdentificationSchema({ type, code }: { type: string; code: string }, context: RefinementCtx) {
    if (!type) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['type'],
            message: `Identification is required`,
        });
        return z.NEVER;
    }

    const typeUppercase = type.toUpperCase();
    const definition = identificationTypesData.find(({ code }) => code.toUpperCase() === typeUppercase);

    if (!definition) {
        context.addIssue({
            code: z.ZodIssueCode.invalid_enum_value,
            path: ['type'],
            received: type,
            options: Object.values(IdentificationType),
        });
        return z.NEVER;
    }

    const { minLength, maxLength, format, regex } = definition;

    if (regex) {
        const valid = new RegExp(regex).test(code);

        if (!valid) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['code'],
                message: `Not a valid ${type}`,
            });
        }

        return z.NEVER;
    }

    if (format) {
        const { type, regex } = formatRegexes[format];

        if (!regex.test(code)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['code'],
                message: `Value must be ${type}`,
            });
        }
    }

    if (code.length < minLength) {
        context.addIssue({
            code: z.ZodIssueCode.too_small,
            path: ['code'],
            minimum: minLength,
            inclusive: true,
            exact: false,
            type: 'string',
        });
    }

    if (code.length > maxLength) {
        context.addIssue({
            code: z.ZodIssueCode.too_big,
            path: ['code'],
            maximum: maxLength,
            inclusive: true,
            exact: false,
            type: 'string',
        });
    }
}
